import React from 'react';
import ValidationRules from './ValidationRules';
import _ from 'lodash';
import ValidationStyle from './ValidationStyle';
import FormError from "../../components/form/form-error/FormError";
import objectAtPath from "../../common/ObjectAtPath";

/**
 * this is the engine that collects the rules and validates a field/value against the rules
 */
export default class ValidationEngine {
	constructor(fieldsAndRules) {
		// key is the field name, value is an array of rules/validation status
		this.fieldRules = {};
		this.lastData = {};
		this.validationStyle = ValidationStyle.ALWAYS;

		_.forEach(fieldsAndRules, (value, key) => this.addRulesForField(key, value));
	}

	/**
	 * get the current validation state of the fields
	 *
	 * @param specificField can pass a field here to get information on just that one field instead of all fields
	 * @return object of field => validation state OR true/array for specificField; validation state is true for valid or an array of objects with the failed ruletype and message per rule
	 */
	getValidState(specificField = undefined) {
		const validStates = _.reduce(this.fieldRules, (result, value, key) => {
			// create a map of the dataField and its validation result
			let validationResult = true;
			if (this.fieldRules[key].validState === true) {
				result[key] = true;
			} else {
				// convert failed rules to errorMessage/ruleType objects
				validationResult = this.fieldRules[key].validState && this.fieldRules[key].validState.map(rule => {
					return {
						// last data is the data that was last sent to form validation
						errorMessage: rule.errorMessage(this.lastData[key]),
						// type of the rule that failed
						ruleType: rule.options.ruleType,
					}
				})
			}

			result[key] = validationResult;

			return result;
		}, {});
		// if getting a specific field, and that field is not in the rules, then the field is always valid
		return specificField ? (validStates[specificField] === undefined ? true : validStates[specificField]) : validStates;
	}

	/**
	 * check if a field has a particular rule. helpful for knowing if a required rule is present.
	 *
	 * @param field the field in question
	 * @param ruleType type of rule: use ValidationRules enum or your own custom types
	 * @return boolean
	 */
	fieldHasRule(field, ruleType) {
		const validationRulesForField = this.fieldRules[field];
		return !!(validationRulesForField && _.some(validationRulesForField.rules, rule => {
			return rule.options.ruleType === ruleType;
		}));
	}

	/**
	 * if the rule is a string then get the validation function for it (easy, for joe, use case of simple tests like REQUIRED that take no parameters)
	 *
	 * @param rules the rules or constants defining which rule
	 */
	convertToValidationRules(rules) {
		return _.castArray(rules).filter(rule => rule).map(rule => _.isString(rule) ? ValidationRules[rule]() : rule);
	}

	/**
	 * add a set of fields to a field
	 *
	 * @param field which field gets the rule(s)
	 * @param rules array|ValidationRule the rules or rule to add to the field
	 */
	addRulesForField(field, rules) {
		if (!this.fieldRules[field]) {
			this.fieldRules[field] = {
				// rules to check for this field: always ValidationRule objects
				rules: [],

				// array of rules that failed: always ValidationRule objects
				validState: undefined,
			};
		}
		this.fieldRules[field].rules = this.fieldRules[field].rules.concat(this.convertToValidationRules(rules));
	}

	/**
	 * remove a rule for a field
	 * @param field the field from which to remove a rule
	 * @param ruleTypes what type of rule to remove (use ValidationType constants); or undefined to have all rules removed
	 */
	removeRulesForField(field, ruleTypes) {
		const rules = this.fieldRules[field];
		const removeRuleTypes = ruleTypes === undefined ? [] : _.castArray(ruleTypes);
		if (rules && rules.rules) {
			this.fieldRules[field].rules = rules.rules.filter(rule => ruleTypes && !removeRuleTypes.includes(rule.ruleType));
		}
	}

	/**
	 * Validate the fields in a form. The passed in object should have all the fields of the form.
	 * The key names will be matched with the names of the fields in the validation rules.
	 *
	 * @param formData object contains all the fields in the form; validation rules will be applied to each field
	 * @param fields can validate on a single field or an array of fields or if passed falsey then it validates all fields
	 * @return boolean true if valid, falsey if failed
	 */
	isValid(formData, fields) {
		// check if field parameter is passed in
		let fieldsToValidate = _.castArray(fields ? fields : Object.keys(this.fieldRules));

		// only remember last data of the passed in fields
		fieldsToValidate.forEach(field => this.lastData[field] = _.clone(objectAtPath(formData, field)));

		// validate given fields
		return fieldsToValidate.reduce((isValid, key) => {
			const rules = this.fieldRules[key] ? this.fieldRules[key].rules : [];
			const failedRules = _.filter(rules, rule => !rule.isValid(objectAtPath(formData, key), formData));
			const fieldValid = _.isEmpty(failedRules) ? true : failedRules;
			this.fieldRules[key] && (this.fieldRules[key].validState = fieldValid);
			return isValid && (fieldValid === true);
		}, true);
	}

	setValidationStyle(validationStyle) {
		if (!Object.values(ValidationStyle).includes(validationStyle)) {
			console.error(`Unknown validationStyle; Make sure to use ValidationStyle values: ($validationStyle)`)
		}
		this.validationStyle = validationStyle;
	}

	/**
	 * get an error message for the current validation rules
	 * getFormError wraps this message in a <FormError> component which is useful when this is being called by inputs
	 *
	 * @param field the field to check
	 * @param hasBlurred has this field been blurred? (if so, it always validates instead of following validationStyle)
	 * @param errorMessageOverride use this error message instead of the normal one if the fields is invalid
	 * @return {*}
	 */
	getError(field, hasBlurred = false, errorMessageOverride = undefined) {
		let errorMessage = errorMessageOverride;
		if (!errorMessage) {
			const validState = this.getValidState(field);
			if (validState !== true && (
				this.validationStyle === ValidationStyle.ALWAYS ||
				(this.validationStyle === ValidationStyle.BLURRED && hasBlurred))
			) {
				errorMessage = validState[0].errorMessage;
			}
		}
		return errorMessage;
	}

	/**
	 * get the first form error for the specified field
	 *
	 * @param field {string} - the field to get
	 * @param hasBlurred {boolean} - the field has lost focus / blurred
	 * @param errorMessageOverride {string} - a error message that will override any validation messages
	 * @return {undefined}
	 */
	getFormError(field, hasBlurred = false, errorMessageOverride = undefined) {
		let errorMessage = this.getError(field, hasBlurred, errorMessageOverride);
		return errorMessage ? <FormError field={field}>{errorMessage}</FormError> : errorMessage;
	}
}
